import '../App.css';
import {
    Box, Button,
    createTheme, CssBaseline, ThemeProvider,
    Stack, Typography, useMediaQuery,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Chip, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import {Add, BarChart, Delete, Edit, Visibility, Close, Download} from "@mui/icons-material";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import SideBar from "../Componant/sidebar/Sidebar";
import QrCodeWizard from "../Componant/QrCode/QrCodeWizard";
import {createQrCode, deleteQrCode, getQrCodeImage, listQrCodes, updateQrCode} from "../Utils/qrcodesApi";

function MyQrCodesPage() {
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const isComputer = useMediaQuery('(min-width:900px)');
    const [sidebarIsOpen, setSidebarIsOpen] = useState();
    const navigate = useNavigate();

    const [qrcodes, setQrcodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editingQrCode, setEditingQrCode] = useState(null);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [previewFormat, setPreviewFormat] = useState('png');

    const darkTheme = createTheme({
        palette: {
            mode: prefersDarkMode ? 'dark' : 'light',
            background: {default: prefersDarkMode ? '#1A1A1A' : '#f5f5f5'},
            primary: {main: '#FF7233', light: '#FF7233', dark: '#e05a1a'},
        },
        typography: {
            fontFamily: '"Poppins", sans-serif',
            allVariants: {color: prefersDarkMode ? '#cecece' : '#000000FF'},
        },
    });

    const refresh = () => {
        setLoading(true);
        listQrCodes()
            .then(setQrcodes)
            .catch((error) => console.error('Erreur lors du chargement des QR codes:', error))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        refresh();
    }, []);

    const openCreateForm = () => {
        setEditingQrCode(null);
        setFormOpen(true);
    };

    const openEditForm = (qrcode) => {
        setEditingQrCode(qrcode);
        setFormOpen(true);
    };

    const closeForm = () => setFormOpen(false);

    const handleSubmit = async (payload) => {
        try {
            if (editingQrCode) {
                await updateQrCode(editingQrCode.id, payload);
            } else {
                await createQrCode(payload);
            }
            setFormOpen(false);
            refresh();
        } catch (error) {
            console.error('Erreur lors de l\'enregistrement du QR code:', error);
            alert(error.response?.data?.error || 'Erreur lors de l\'enregistrement du QR code');
        }
    };

    const handleDelete = async (qrcode) => {
        if (!window.confirm(`Supprimer "${qrcode.name}" ? Cette action est irréversible.`)) return;

        try {
            await deleteQrCode(qrcode.id);
            refresh();
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
            alert('Erreur lors de la suppression du QR code');
        }
    };

    const handlePreview = async (qrcode) => {
        try {
            const {image, format} = await getQrCodeImage(qrcode.id);
            setPreviewImage(image);
            setPreviewFormat(format);
            setPreviewOpen(true);
        } catch (error) {
            console.error('Erreur lors de la génération de l\'aperçu:', error);
            alert('Erreur lors de la génération de l\'aperçu');
        }
    };

    const handleDownloadPreview = () => {
        if (!previewImage) return;
        const link = document.createElement('a');
        link.href = previewImage;
        link.download = `qrcode.${previewFormat}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getContainerClass = () => {
        const classes = [];
        if (!isComputer) {
            classes.push("mobile");
        } else {
            classes.push(sidebarIsOpen ? 'activated' : 'disactivated');
        }
        return classes.join(' ');
    };

    return (
        <div className="App">
            <ThemeProvider theme={darkTheme}>
                <SideBar sidebarIsOpen={sidebarIsOpen} setSidebarIsOpen={setSidebarIsOpen} isComputer={isComputer}/>
                <CssBaseline/>

                <Box paddingTop={'5%'} id='Md-Container' className={getContainerClass()}>
                    <Box sx={{padding: 4, margin: '0 auto', width: '100%'}}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h4">Mes QR codes</Typography>
                            <Button variant="contained" color="primary" startIcon={<Add/>} onClick={openCreateForm}>
                                Créer un QR code
                            </Button>
                        </Stack>

                        <Box>
                            {loading ? (
                                <Typography>Chargement...</Typography>
                            ) : qrcodes.length === 0 ? (
                                <Typography color="text.secondary">
                                    Aucun QR code pour le moment. Créez-en un pour commencer.
                                </Typography>
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Nom</TableCell>
                                                <TableCell>URL cible</TableCell>
                                                <TableCell>Domaine</TableCell>
                                                <TableCell>Statut</TableCell>
                                                <TableCell align="right">Scans</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {qrcodes.map((qrcode) => {
                                                const isExpired = !!qrcode.expiresAt && new Date(qrcode.expiresAt) < new Date();
                                                const limitReached = qrcode.maxScans != null && qrcode.scanCount >= qrcode.maxScans;
                                                return (
                                                <TableRow key={qrcode.id} hover>
                                                    <TableCell>{qrcode.name}</TableCell>
                                                    <TableCell sx={{maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                                        {qrcode.targetUrl}
                                                    </TableCell>
                                                    <TableCell>{qrcode.trackingEnabled ? (qrcode.domain || 'Par défaut') : '—'}</TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={1}>
                                                            <Chip
                                                                size="small"
                                                                label={qrcode.trackingEnabled ? 'Suivi actif' : 'Sans suivi'}
                                                                color={qrcode.trackingEnabled ? 'primary' : 'default'}
                                                                variant={qrcode.trackingEnabled ? 'filled' : 'outlined'}
                                                            />
                                                            {!qrcode.isActive && (
                                                                <Chip size="small" label="En pause" color="warning" variant="outlined"/>
                                                            )}
                                                            {isExpired && (
                                                                <Chip size="small" label="Expiré" color="error" variant="outlined"/>
                                                            )}
                                                            {!isExpired && limitReached && (
                                                                <Chip size="small" label="Limite de scans atteinte" color="error" variant="outlined"/>
                                                            )}
                                                            {!isExpired && !limitReached && (qrcode.expiresAt || qrcode.maxScans != null) && (
                                                                <Chip size="small" label="Éphémère" color="secondary" variant="outlined"/>
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell align="right">{qrcode.trackingEnabled ? qrcode.scanCount : '—'}</TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="Aperçu">
                                                            <IconButton onClick={() => handlePreview(qrcode)}>
                                                                <Visibility fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                        {qrcode.trackingEnabled && (
                                                            <Tooltip title="Statistiques">
                                                                <IconButton onClick={() => navigate(`/my-qrcodes/${qrcode.id}`)}>
                                                                    <BarChart fontSize="small"/>
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title="Modifier">
                                                            <IconButton onClick={() => openEditForm(qrcode)}>
                                                                <Edit fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Supprimer">
                                                            <IconButton onClick={() => handleDelete(qrcode)}>
                                                                <Delete fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Dialog open={formOpen} onClose={closeForm} maxWidth="md" fullWidth>
                    <DialogTitle>
                        {editingQrCode ? 'Modifier le QR code' : 'Créer un QR code'}
                        <IconButton onClick={closeForm} sx={{position: 'absolute', right: 8, top: 8}}>
                            <Close/>
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <QrCodeWizard initial={editingQrCode} onSubmit={handleSubmit} onCancel={closeForm}/>
                    </DialogContent>
                </Dialog>

                <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        Aperçu du QR code
                        <IconButton onClick={() => setPreviewOpen(false)} sx={{position: 'absolute', right: 8, top: 8}}>
                            <Close/>
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Box display="flex" justifyContent="center">
                            {previewImage && (
                                <Box component="img" src={previewImage} alt="QR code" sx={{maxWidth: '100%', borderRadius: 2}}/>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDownloadPreview} startIcon={<Download/>}>Télécharger</Button>
                    </DialogActions>
                </Dialog>
            </ThemeProvider>
        </div>
    );
}

export default MyQrCodesPage;
